import React, { useContext, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import DealerChipContext from 'contexts/DealerChipContext';
import { useOrientation } from 'hooks/useOrientation';
import { useTableSelector } from 'hooks/useTableSelector';
import { resolveElementPosition } from 'hooks/useFloatingRegistry';
import { useGsapTimeline } from 'animations/useGsapTimeline';
import { DealerChip } from 'components/Seat/components';
import { DealerAnimationContext } from 'contexts/DealerAnimationContext';
import { useShowAllDealers } from 'contexts/ShowAllDealersContext';
import { TableZ } from 'styles/zIndex';

export const DEALER_DELAY = 1000;

export const DealerButton = () => {
  const dealer = useTableSelector((table) => table.game.dealer);
  // Demo-only: when every seat already renders its own static dealer chip
  // ("All dealers"), the single floating/animated button is redundant and shows
  // up as an extra chip on top of the per-seat ones, so suppress it.
  const showAllDealers = useShowAllDealers();
  const { refs } = useContext(DealerChipContext);
  const { isAnimating, setIsAnimating, setDealer } = useContext(DealerAnimationContext);
  const [animate, setAnimate] = useState(false);

  // Visibility decision is computed server-side in cash-game-broadcast and
  // delivered as `game.dealerVisible`. The client used to re-derive it from
  // hand / isOnBreak / per-seat status — that logic has been moved to
  // gameTransforms.js so the React side just consumes the boolean.
  const dealerVisible = useTableSelector(
    (table) => table.game.dealerVisible
  );

  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';

  const [dealerPosition, setDealerPosition] = useState<{ leftPct: number; topPct: number } | null>(null);

  const dealerRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const spinRef = useRef<HTMLDivElement>(null);
  const prevDealerRef = useRef<number>(0);
  const { createTimeline } = useGsapTimeline();


  useEffect(() => {
    const dealerSeatNumber = +dealer;

    if (!dealerVisible || dealerSeatNumber <= 0) {
      // Clear any previously-rendered button — without this, the button stays
      // pinned to its last position when the table empties out.
      if (dealerPosition !== null) {
        setDealerPosition(null);
        prevDealerRef.current = 0;
      }
      return;
    }

    const newPos = resolveElementPosition(refs.get(dealerSeatNumber));
    if (!newPos) return;

    const shouldAnimate = prevDealerRef.current !== 0 && prevDealerRef.current !== dealerSeatNumber;
    const prevDealer = prevDealerRef.current;
    prevDealerRef.current = dealerSeatNumber;

    if (!dealerRef.current || !chipRef.current) {
      setDealerPosition(newPos);
      return;
    }

    if (!shouldAnimate && !animate) {
      gsap.set(dealerRef.current, {
        left: `${newPos.leftPct}%`,
        top: `${newPos.topPct}%`
      });
      setDealerPosition(newPos);
      return;
    } else {
      if(!animate) {
        const prevPos = resolveElementPosition(refs.get(prevDealer));
        if(prevPos) {
          gsap.set(dealerRef.current, {
            left: `${prevPos.leftPct}%`,
            top: `${prevPos.topPct}%` 
          });
          setDealerPosition(prevPos);
        }

        setIsAnimating(true);
      }
    }
    
  }, [dealer, dealerVisible, refs.size, animate]);

  useEffect(() => {
    if(!isAnimating || animate) {
      return;
    }
    setAnimate(true);

    const dealerSeatNumber = +dealer;

    const newPos = resolveElementPosition(refs.get(dealerSeatNumber));
    if (!newPos) return;

    // Calculate distance to determine travel time and spin count
    const oldLeft = dealerPosition?.leftPct ?? newPos.leftPct;
    const oldTop = dealerPosition?.topPct ?? newPos.topPct;
    const dx = newPos.leftPct - oldLeft;
    const dy = newPos.topPct - oldTop;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Scale duration and spins by distance (min 0.4s, max 1.2s)
    const travelDuration = Math.min(Math.max(distance / 60, 0.4), 1.2);
    // More distance = more spins (1-4 full turns)
    const spinCount = Math.min(Math.max(Math.round(distance / 15), 1), 4);
    const totalSpin = spinCount * 360;
    const lift = Math.min(Math.max(distance / 8, 8), 18);

    const tl = createTimeline({
      onComplete: () => {
        setIsAnimating(false);
        setAnimate(false);
        setDealerPosition(newPos);
        setDealer(dealerSeatNumber);
      }
    });

    // Lift off
    tl.to(chipRef.current, {
      scale: 1.6 + (distance / 100) * 0.4,
      y: -lift,
      filter: 'drop-shadow(0 14px 14px rgba(0,0,0,0.45))',
      duration: 0.18,
      ease: 'power2.out'
    })
    // Move + spin — duration proportional to distance
    .to(dealerRef.current, {
      left: `${newPos.leftPct}%`,
      top: `${newPos.topPct}%`,
      duration: travelDuration,
      ease: 'power2.inOut'
    }, '<0.1')
    .to(spinRef.current, {
      rotationY: totalSpin,
      duration: travelDuration,
      ease: 'none'
    }, '<')
    // Land
    .to(chipRef.current, {
      scale: 1,
      y: 0,
      filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.28))',
      duration: 0.22,
      ease: 'back.out(1.5)'
    })
    .set(chipRef.current, { y: 0, filter: 'none' })
    .set(spinRef.current, { rotationY: 0 });
  }, [dealer, isAnimating, animate]);

  if (showAllDealers || !dealerPosition) {
    return null;
  }

  return (
    <div
      ref={dealerRef}
      style={{
        position: 'absolute',
        left: `${dealerPosition.leftPct}%`,
        top: `${dealerPosition.topPct}%`,
        transform: 'translate(-50%, -50%)',
        // TABLE element band — under every seat. The button is only visible
        // while it travels between seats; at rest each seat draws its own
        // marker under its own cards (getSeatDealerOffset).
        zIndex: TableZ.tableElement,
        perspective: '200px',
        opacity: animate ? 1 : 0,
        borderRadius: '50%',
      }}>
      <div ref={chipRef} style={{ transformStyle: 'preserve-3d', transformOrigin: 'center center' }}>
        <div ref={spinRef} style={{ transformStyle: 'preserve-3d', transformOrigin: 'center center' }}>
          <DealerChip isPortrait={isPortrait} />
        </div>
      </div>
    </div>
  );
};
